import { Home01Icon } from "@hugeicons-pro/core-solid-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Badge,
  BadgeSize,
  BadgeVariant,
  Button,
  ButtonSize,
  ButtonVariant
} from "shared/components";
import { Avatar } from "shared/components/Avatar";
import { cx } from "shared/utils";
import { getErrorMessage } from "utils/error";
import { formatCalendar } from "../helpers/format-calendar";
import {
  isWaitingOnPartner,
  rentalVerificationCardCopy,
  rentalVerificationLocation,
  type RentalVerificationBadgeTone
} from "../helpers/rental-verification";
import { Notification } from "../types";

const BADGE_VARIANTS: Record<RentalVerificationBadgeTone, BadgeVariant> = {
  warning: BadgeVariant.WARNING,
  success: BadgeVariant.SUCCESS,
  error: BadgeVariant.ERROR,
  base: BadgeVariant.BASE
};

export const RentalVerificationNotificationCard = ({
  item
}: {
  item: Notification;
}) => {
  const queryClient = useQueryClient();
  const { id, read_at, metadata, created_at } = item;

  const { mutate: markAsRead } = useMutation({
    mutationKey: ["NOTIFICATIONS", "MARK_AS_READ"],
    mutationFn: (ids: number[]) =>
      instance.post("/portal/v1/notifications/mark_as_read", { ids }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["NOTIFICATIONS"] });
      queryClient.invalidateQueries({
        queryKey: ["UNREAD_NOTIFICATIONS_COUNT"]
      });
      queryClient.invalidateQueries({
        queryKey: ["TOTAL_UNREAD_NOTIFICATIONS"]
      });
      queryClient.invalidateQueries({ queryKey: ["NEWEST_NOTIFICATIONS"] });
    },
    onError: (error) => toast.error(getErrorMessage(error))
  });

  const handleClick = () => {
    if (!read_at) markAsRead([id]);
  };

  const copy = rentalVerificationCardCopy(item);
  const applicantName = metadata.applicant_name?.trim() || "Applicant";
  const location = rentalVerificationLocation(item);
  const surveyPath = item.url || `/land-x/${item.metadata.survey_token ?? ""}`;
  const opensSurvey = isWaitingOnPartner(item);

  const row = (
    <>
      <span
        aria-hidden="true"
        className={cx(
          "size-3 rounded-full shrink-0",
          read_at ? "bg-alpha-40" : "bg-primary-500"
        )}
      />
      <Avatar name={applicantName} size={48} className="font-bold shrink-0" />
      <div className="space-y-1 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <p className="text-base font-bold text-content-1000 truncate">
            {applicantName}
          </p>
          {copy.badge ? (
            <Badge
              size={BadgeSize.SM}
              variant={BADGE_VARIANTS[copy.badge.tone]}
              className="shrink-0"
            >
              {copy.badge.text}
            </Badge>
          ) : null}
        </div>
        <p className="text-sm text-content-400 line-clamp-2">{copy.line}</p>
        {copy.detail ? (
          <p className="text-sm text-content-400 line-clamp-2">{copy.detail}</p>
        ) : null}
        {location ? (
          <p className="text-sm text-content-400 truncate">{location}</p>
        ) : null}
      </div>
    </>
  );

  return (
    <div
      className={cx(
        "w-full space-y-4 bg-white p-6",
        opensSurvey && "hover:bg-alpha-40"
      )}
    >
      <div className="flex w-full items-center justify-between gap-4">
        <div className="flex items-center gap-2.5 min-w-0">
          <HugeiconsIcon
            icon={Home01Icon}
            size={16}
            className="text-content-200 shrink-0"
            aria-hidden
          />
          <p className="truncate text-sm font-medium text-content-300">
            {copy.label}
          </p>
        </div>
        <p className="shrink-0 text-sm font-medium text-content-300">
          {formatCalendar(created_at)}
        </p>
      </div>

      <div className="flex items-center gap-6">
        {opensSurvey ? (
          <Link
            to={surveyPath}
            onClick={handleClick}
            className="flex min-w-0 flex-1 items-center gap-6 cursor-pointer"
            target="_blank"
          >
            {row}
          </Link>
        ) : (
          <div
            role="button"
            tabIndex={0}
            onClick={handleClick}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") handleClick();
            }}
            className="flex min-w-0 flex-1 items-center gap-6"
          >
            {row}
          </div>
        )}

        {copy.button ? (
          <Link
            to={surveyPath}
            onClick={handleClick}
            tabIndex={-1}
            className="shrink-0"
            target="_blank"
          >
            <Button
              type="button"
              variant={ButtonVariant.BASE}
              size={ButtonSize.SM}
              className="shadow-xs"
            >
              {copy.button}
            </Button>
          </Link>
        ) : null}
      </div>
    </div>
  );
};
